import { useEffect, useState, type FormEvent } from 'react';
import axios from 'axios';
import { api } from '../api/client';
import type { Puzzle } from '../api/types';

interface Props {
  puzzle: Puzzle;
  onSaved(): void;
}

interface FormState {
  hint_1: string;
  hint_2: string;
  hint_3: string;
  hint_4: string;
}

function fromPuzzle(puzzle: Puzzle): FormState {
  return {
    hint_1: puzzle.hint_1 ?? '',
    hint_2: puzzle.hint_2 ?? '',
    hint_3: puzzle.hint_3 ?? '',
    hint_4: puzzle.hint_4 ?? '',
  };
}

function hasAnyHint(p: Puzzle): boolean {
  return Boolean(p.hint_1 || p.hint_2 || p.hint_3 || p.hint_4);
}

/**
 * Inline-Anzeige der Tipps mit Bearbeiten-Modus. Bestehende Tipps
 * werden in einer nummerierten Liste gerendert; per Bearbeiten-Knopf
 * öffnet sich ein Form mit 4 Textareas. Wenn noch keine Tipps da
 * sind, zeigt sich direkt das leere Form.
 */
export function HintsInline({ puzzle, onSaved }: Props) {
  const [editing, setEditing] = useState(!hasAnyHint(puzzle));
  const [form, setForm] = useState<FormState>(fromPuzzle(puzzle));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setForm(fromPuzzle(puzzle));
    setEditing(!hasAnyHint(puzzle));
    setError(null);
  }, [puzzle.id, puzzle.hint_1, puzzle.hint_2, puzzle.hint_3, puzzle.hint_4]);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((s) => ({ ...s, [key]: value }));

  const submit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      // Server-Upsert mit COALESCE überschreibt nicht mit null. Damit
      // ein leergetypter Tipp wirklich entfernt werden kann, schicken
      // wir leere Strings als null mit — der Server hat in diesem
      // POST-Pfad einen MERGE-mit-COALESCE, der null als "nicht
      // ändern" interpretiert. Für unseren Use-Case (Tipps befüllen
      // oder ergänzen) ist das die richtige Semantik.
      await api.post('/puzzle', {
        puzzle_number: puzzle.puzzle_number,
        puzzle_date: puzzle.puzzle_date.slice(0, 10),
        hint_1: form.hint_1.trim() || null,
        hint_2: form.hint_2.trim() || null,
        hint_3: form.hint_3.trim() || null,
        hint_4: form.hint_4.trim() || null,
      });
      onSaved();
      setEditing(false);
    } catch (err) {
      const text = axios.isAxiosError(err) && err.response?.data?.error
        ? String(err.response.data.error)
        : 'Speichern fehlgeschlagen';
      setError(text);
    } finally {
      setBusy(false);
    }
  };

  if (!editing) {
    return (
      <>
        {hasAnyHint(puzzle) ? (
          <ul className="hints">
            {[puzzle.hint_1, puzzle.hint_2, puzzle.hint_3, puzzle.hint_4].map(
              (h, i) =>
                h ? (
                  <li key={i} className="hints__item">
                    <span className="hints__num">{i + 1}</span>
                    <span className="hints__text">{h}</span>
                  </li>
                ) : null,
            )}
          </ul>
        ) : (
          <p className="today__no-hints">Es wurden keine Hinweise eingetragen.</p>
        )}
        <button
          type="button"
          className="button button--ghost hints__edit"
          onClick={() => setEditing(true)}
        >
          {hasAnyHint(puzzle) ? 'Hinweise bearbeiten' : 'Hinweise eintragen'}
        </button>
      </>
    );
  }

  return (
    <form className="hints-inline" onSubmit={submit} data-no-swipe>
      {([1, 2, 3, 4] as const).map((n) => (
        <label key={n} className="field">
          <span className="field__label">Hinweis {n}</span>
          <textarea
            className="field__input field__input--textarea hints-inline__input"
            rows={3}
            placeholder="Mehrzeiliger Hinweis möglich"
            value={form[`hint_${n}` as const]}
            onChange={(e) => update(`hint_${n}` as const, e.target.value)}
          />
        </label>
      ))}
      {error && <p className="entry-form__error" role="alert">{error}</p>}
      <div className="hints-inline__buttons">
        <button type="submit" className="button button--primary" disabled={busy}>
          {busy ? 'Speichere …' : 'Speichern'}
        </button>
        {hasAnyHint(puzzle) && (
          <button
            type="button"
            className="button button--ghost"
            onClick={() => {
              setForm(fromPuzzle(puzzle));
              setEditing(false);
              setError(null);
            }}
            disabled={busy}
          >
            Abbrechen
          </button>
        )}
      </div>
    </form>
  );
}
